/**
 * EventCommsWorkspace — DCC-02
 * Event-scoped canonical communications workspace.
 * Route: embedded in EventMasterWorkspace (Communications tab)
 *
 * Features:
 *  - Communication history table (channel, recipient, status, timestamps)
 *  - Compose modal (thread selector, channel, recipient, subject, body)
 *  - Send / retry flow
 *  - Template browser
 *
 * Authorization:
 *  - comms.event.read  → view history + templates
 *  - comms.event.send  → compose + dispatch
 *  - comms.template.manage → create templates
 */
import { useState, useEffect, useCallback } from "react";
import {
  Mail, MessageSquare, Send, Plus, RefreshCw, Clock,
  CheckCircle2, XCircle, AlertTriangle, Loader2,
  ChevronRight, Eye, FileText, User,
} from "lucide-react";
import { commsApi, eventApi } from "@/services/api";
import { hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const STATUS_STYLE = {
  DRAFT:     "bg-slate-100 text-slate-600 border-slate-200",
  QUEUED:    "bg-amber-50 text-amber-700 border-amber-200",
  SENT:      "bg-blue-50 text-blue-700 border-blue-200",
  DELIVERED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  FAILED:    "bg-red-50 text-red-700 border-red-200",
  BOUNCED:   "bg-orange-50 text-orange-700 border-orange-200",
};

const CHANNEL_ICON = {
  EMAIL:    <Mail className="w-3.5 h-3.5" />,
  WHATSAPP: <MessageSquare className="w-3.5 h-3.5" />,
  MANUAL:   <FileText className="w-3.5 h-3.5" />,
};

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLE[status] || "bg-slate-100 text-slate-500"}`}
      data-testid={`comm-status-${status?.toLowerCase()}`}
    >
      {status}
    </span>
  );
}

// ── Compose Modal ─────────────────────────────────────────────────────────────

function ComposeModal({ eid, threads, onClose, onSent }) {
  const [channel,         setChannel]         = useState("EMAIL");
  const [threadId,        setThreadId]        = useState(threads[0]?.id || "");
  const [recipientEmail,  setRecipientEmail]  = useState("");
  const [recipientPhone,  setRecipientPhone]  = useState("");
  const [recipientName,   setRecipientName]   = useState("");
  const [subject,         setSubject]         = useState("");
  const [bodyText,        setBodyText]        = useState("");
  const [sending,         setSending]         = useState(false);

  const handleSend = async () => {
    if (!threadId) return toast.error("Select a thread");
    if (channel === "EMAIL" && !recipientEmail) return toast.error("Recipient email required");
    if (channel === "EMAIL" && !subject) return toast.error("Subject required");
    if (channel === "WHATSAPP" && !recipientPhone) return toast.error("Recipient phone required");
    if (!bodyText.trim()) return toast.error("Message body required");

    setSending(true);
    try {
      const draftRes = await commsApi.compose(eid, {
        thread_id:       threadId,
        channel,
        recipient_email: channel === "EMAIL"    ? recipientEmail : null,
        recipient_phone: channel === "WHATSAPP" ? recipientPhone : null,
        recipient_name:  recipientName || null,
        subject:         channel === "EMAIL"    ? subject : null,
        body_text:       bodyText,
      });
      const draft = draftRes.data;
      const sentRes = await commsApi.send(eid, draft.id, { force: false });
      const sent = sentRes.data;
      toast.success(`Message ${sent.status.toLowerCase()} via ${channel}`);
      onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6 flex flex-col gap-4"
        data-testid="compose-modal"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-800 text-lg">Compose Communication</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
        </div>

        {/* Channel */}
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Channel</Label>
          <div className="flex gap-2">
            {["EMAIL", "WHATSAPP", "MANUAL"].map(c => (
              <button
                key={c}
                data-testid={`channel-${c.toLowerCase()}`}
                onClick={() => setChannel(c)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                  channel === c
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300"
                }`}
              >
                {CHANNEL_ICON[c]}
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Thread */}
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Thread <span className="text-red-400">*</span></Label>
          <select
            value={threadId}
            onChange={e => setThreadId(e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            data-testid="thread-selector"
          >
            {threads.length === 0 && <option value="">No threads available</option>}
            {threads.map(t => (
              <option key={t.id} value={t.id}>{t.thread_name || t.name || t.thread_type} ({t.thread_type})</option>
            ))}
          </select>
        </div>

        {/* Recipient */}
        <div className="grid grid-cols-2 gap-3">
          {channel === "EMAIL" && (
            <div className="col-span-2">
              <Label className="text-xs text-slate-500 mb-1 block">Recipient Email <span className="text-red-400">*</span></Label>
              <Input
                value={recipientEmail}
                onChange={e => setRecipientEmail(e.target.value)}
                placeholder="recipient@example.com"
                data-testid="recipient-email"
                className="text-sm"
              />
            </div>
          )}
          {channel === "WHATSAPP" && (
            <div className="col-span-2">
              <Label className="text-xs text-slate-500 mb-1 block">Recipient Phone <span className="text-red-400">*</span></Label>
              <Input
                value={recipientPhone}
                onChange={e => setRecipientPhone(e.target.value)}
                placeholder="+91 98765 43210"
                data-testid="recipient-phone"
                className="text-sm"
              />
            </div>
          )}
          <div className={channel === "MANUAL" ? "col-span-2" : "col-span-2"}>
            <Label className="text-xs text-slate-500 mb-1 block">Recipient Name</Label>
            <Input
              value={recipientName}
              onChange={e => setRecipientName(e.target.value)}
              placeholder="Optional display name"
              data-testid="recipient-name"
              className="text-sm"
            />
          </div>
        </div>

        {/* Subject (email only) */}
        {channel === "EMAIL" && (
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">Subject <span className="text-red-400">*</span></Label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Message subject"
              data-testid="comm-subject"
              className="text-sm"
            />
          </div>
        )}

        {/* Body */}
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Message Body <span className="text-red-400">*</span></Label>
          <Textarea
            value={bodyText}
            onChange={e => setBodyText(e.target.value)}
            placeholder="Type your message here..."
            rows={5}
            data-testid="comm-body"
            className="text-sm resize-none"
          />
        </div>

        <div className="flex justify-end gap-3 pt-1">
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="compose-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={sending} data-testid="compose-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
            {sending ? "Sending…" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Detail Slide-over ─────────────────────────────────────────────────────────

function CommDetail({ comm, onClose }) {
  if (!comm) return null;
  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col border-l border-slate-200" data-testid="comm-detail-panel">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
        <h3 className="font-semibold text-slate-800">Communication Detail</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
        <div className="flex items-center gap-2">
          <StatusBadge status={comm.status} />
          <span className="text-slate-400 text-xs">{comm.channel}</span>
        </div>
        <div>
          <p className="text-xs text-slate-400 mb-0.5">Recipient</p>
          <p className="text-slate-700">{comm.recipient_email || comm.recipient_phone || "—"}</p>
          {comm.recipient_name && <p className="text-slate-400 text-xs">{comm.recipient_name}</p>}
        </div>
        {comm.subject && (
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Subject</p>
            <p className="text-slate-700 font-medium">{comm.subject}</p>
          </div>
        )}
        <div>
          <p className="text-xs text-slate-400 mb-0.5">Message</p>
          <div className="bg-slate-50 rounded-lg p-3 text-slate-700 whitespace-pre-wrap border border-slate-100">
            {comm.body_text}
          </div>
        </div>
        {comm.provider_message_id && (
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Provider Message ID</p>
            <p className="font-mono text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded">{comm.provider_message_id}</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Created</p>
            <p className="text-slate-600">{fmtDT(comm.created_at)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-0.5">Sent</p>
            <p className="text-slate-600">{fmtDT(comm.sent_at)}</p>
          </div>
        </div>
        {comm.failure_reason && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-600 font-medium mb-0.5">Failure Reason</p>
            <p className="text-red-700 text-xs">{comm.failure_reason}</p>
          </div>
        )}
        <div>
          <p className="text-xs text-slate-400 mb-0.5">Provider</p>
          <p className="text-slate-500">{comm.provider || "—"}</p>
        </div>
      </div>
    </div>
  );
}

// ── Main Workspace ────────────────────────────────────────────────────────────

export default function EventCommsWorkspace({ eid, event, embedded = false }) {
  const { user } = useAuth();
  const canRead       = hasCapability(user, "comms.event.read");
  const canSend       = hasCapability(user, "comms.event.send");
  const canTemplate   = hasCapability(user, "comms.template.manage");

  const [comms,        setComms]        = useState([]);
  const [threads,      setThreads]      = useState([]);
  const [summary,      setSummary]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [showCompose,  setShowCompose]  = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterChannel,setFilterChannel]= useState("");
  const [retrying,     setRetrying]     = useState(null);

  const loadData = useCallback(async () => {
    if (!canRead) { setLoading(false); return; }
    try {
      const params = {};
      if (filterStatus)  params.status  = filterStatus;
      if (filterChannel) params.channel = filterChannel;
      const [commsRes, summaryRes, threadsRes] = await Promise.all([
        commsApi.list(eid, params),
        commsApi.getSummary(eid),
        eventApi.getEventThreads(eid),
      ]);
      setComms(commsRes.data || []);
      setSummary(summaryRes.data);
      const tArr = Array.isArray(threadsRes.data) ? threadsRes.data
        : (threadsRes.data?.threads || []);
      setThreads(tArr);
    } catch {
      toast.error("Failed to load communications");
    } finally {
      setLoading(false);
    }
  }, [eid, canRead, filterStatus, filterChannel]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRetry = async (comm) => {
    setRetrying(comm.id);
    try {
      await commsApi.send(eid, comm.id, { force: true });
      toast.success("Message retried");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying(null);
    }
  };

  if (!canRead) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400" data-testid="comms-no-access">
        You do not have access to the Communications workspace.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="event-comms-workspace">
      {/* ── Summary tiles ── */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total",   value: summary.total,   color: "text-slate-700" },
            { label: "Sent",    value: summary.sent,    color: "text-blue-700"  },
            { label: "Drafts",  value: summary.drafts,  color: "text-amber-700" },
            { label: "Failed",  value: summary.failed,  color: "text-red-700"   },
          ].map(tile => (
            <div key={tile.label} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`comms-tile-${tile.label.toLowerCase()}`}>
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">{tile.label}</p>
              <p className={`text-2xl font-bold mt-1 ${tile.color}`}>{tile.value ?? 0}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            data-testid="filter-status"
          >
            <option value="">All Statuses</option>
            {["DRAFT","QUEUED","SENT","DELIVERED","FAILED","BOUNCED"].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {/* Channel filter */}
          <select
            value={filterChannel}
            onChange={e => setFilterChannel(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
            data-testid="filter-channel"
          >
            <option value="">All Channels</option>
            {["EMAIL","WHATSAPP","MANUAL"].map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <button
            onClick={loadData}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="refresh-comms-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {canSend && (
          <Button
            onClick={() => setShowCompose(true)}
            data-testid="compose-btn"
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Compose
          </Button>
        )}
      </div>

      {/* ── Communications table ── */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
        </div>
      ) : comms.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 flex flex-col items-center justify-center py-16 gap-3" data-testid="comms-empty-state">
          <Send className="w-10 h-10 text-slate-300" />
          <p className="text-slate-500 font-medium">No communications yet</p>
          {canSend && (
            <p className="text-slate-400 text-sm">Click <strong>Compose</strong> to send the first message for this event.</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="comms-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Channel</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Recipient</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Subject / Preview</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Sent</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {comms.map(comm => (
                <tr
                  key={comm.id}
                  className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedComm(selectedComm?.id === comm.id ? null : comm)}
                  data-testid={`comm-row-${comm.id}`}
                >
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5 text-slate-600">
                      {CHANNEL_ICON[comm.channel]}
                      <span className="text-xs">{comm.channel}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700 max-w-[160px] truncate">
                    {comm.recipient_email || comm.recipient_phone || <span className="text-slate-400">manual</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 max-w-[220px] truncate">
                    {comm.subject || comm.body_text?.slice(0, 60) || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={comm.status} />
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                    {comm.sent_at ? fmtDT(comm.sent_at) : fmtDT(comm.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                      {comm.status === "FAILED" && canSend && (
                        <button
                          onClick={() => handleRetry(comm)}
                          disabled={retrying === comm.id}
                          className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Retry send"
                          data-testid={`retry-btn-${comm.id}`}
                        >
                          {retrying === comm.id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <RefreshCw className="w-3.5 h-3.5" />
                          }
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedComm(selectedComm?.id === comm.id ? null : comm)}
                        className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg transition-colors"
                        title="View detail"
                        data-testid={`view-comm-btn-${comm.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showCompose && (
        <ComposeModal
          eid={eid}
          threads={threads}
          onClose={() => setShowCompose(false)}
          onSent={loadData}
        />
      )}
      {selectedComm && (
        <CommDetail
          comm={selectedComm}
          onClose={() => setSelectedComm(null)}
        />
      )}
    </div>
  );
}
